/**
 * Profile — meu perfil (dados pessoais).
 */
import { useMutation } from "@tanstack/react-query";
import { Mail, Phone, ShieldCheck, UserCircle } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { DetailCard, DetailLayout, KeyValue } from "@/components/detail-layout";
import { RolePill } from "@/components/status-pill";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetBody,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { updateMe } from "@/lib/api-extras";
import { formatDateTime, formatPhone } from "@/lib/format";
import { useAuth } from "@/stores/auth";

export function ProfilePage() {
  const { user, setUser } = useAuth();
  const [editOpen, setEditOpen] = useState(false);
  const [name, setName] = useState(user?.name ?? "");
  const [phone, setPhone] = useState((user as any)?.phone ?? "");

  const update = useMutation({
    mutationFn: () => updateMe({ name, phone }) as Promise<typeof user>,
    onSuccess: (data) => {
      if (data) setUser(data);
      toast.success("Perfil atualizado");
      setEditOpen(false);
    },
    onError: (e: Error) => toast.error("Falha", { description: e.message }),
  });

  if (!user) return null;

  return (
    <DetailLayout
      eyebrow="Meu perfil"
      visual={
        <Avatar size="xl">
          {user.picture_url && <AvatarImage src={user.picture_url} referrerPolicy="no-referrer" />}
          <AvatarFallback name={user.name ?? user.email} />
        </Avatar>
      }
      title={user.name ?? "Sem nome"}
      subtitle={user.email}
      badges={
        <>
          <RolePill role={user.role} />
          {user.email_verified ? <Badge variant="success" dot>Verificado</Badge> : <Badge variant="warning" dot>Não verificado</Badge>}
        </>
      }
      actions={
        <Button onClick={() => {
          setName(user.name ?? "");
          setPhone((user as any).phone ?? "");
          setEditOpen(true);
        }}>
          Editar perfil
        </Button>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <DetailCard title="Conta">
          <div className="grid grid-cols-2 gap-x-6 gap-y-3">
            <KeyValue label="Nome" value={user.name} />
            <KeyValue label="Email" value={<span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" />{user.email}</span>} />
            <KeyValue label="Telefone" value={<span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" />{formatPhone((user as any).phone)}</span>} />
            <KeyValue label="Papel" value={<RolePill role={user.role} />} />
            <KeyValue label="Provider" value={user.provider} />
            <KeyValue label="Onboarding" value={user.onboarding_completed ? "Concluído" : "Pendente"} />
          </div>
        </DetailCard>

        <DetailCard title="Acesso">
          <div className="grid grid-cols-2 gap-x-6 gap-y-3">
            <KeyValue label="Último login" value={formatDateTime(user.last_login_at)} />
            <KeyValue label="Conta criada" value={formatDateTime(user.created_at)} />
            <KeyValue label="Status" value={user.is_blocked ? "Bloqueado" : user.is_active ? "Ativo" : "Inativo"} />
            <KeyValue label="Email verificado" value={user.email_verified ? "Sim" : "Não"} />
          </div>
        </DetailCard>

        <DetailCard title="Segurança" className="md:col-span-2">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <ShieldCheck className="h-5 w-5 text-emerald-600" />
              <div>
                <p className="font-medium text-sm">Login via SSO Google</p>
                <p className="text-xs text-muted-foreground">
                  Sua conta usa autenticação federada. Senha gerenciada pela Google.
                </p>
              </div>
            </div>
            <Button variant="outline" disabled>Gerenciar 2FA (em breve)</Button>
          </div>
        </DetailCard>
      </div>

      <Sheet open={editOpen} onOpenChange={setEditOpen}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>Editar perfil</SheetTitle>
          </SheetHeader>
          <SheetBody className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Nome</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Telefone</Label>
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+5511..." />
            </div>
            <div className="rounded-xl border border-slate-200/60 dark:border-white/10 bg-slate-50/40 dark:bg-white/[0.02] p-3 text-xs text-muted-foreground">
              <p className="inline-flex items-center gap-1.5">
                <UserCircle className="h-3 w-3" /> Email e papel são gerenciados pelo admin.
              </p>
            </div>
          </SheetBody>
          <SheetFooter>
            <SheetClose asChild><Button variant="outline">Cancelar</Button></SheetClose>
            <Button onClick={() => update.mutate()} disabled={update.isPending}>
              {update.isPending ? "Salvando..." : "Salvar"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </DetailLayout>
  );
}
